import { cn } from '@/utils/tailwind.util';

/**
 * 배너 이미지가 없을 때의 기본 배경.
 *
 * 폴더 배너로 쓰던 `assets/folder/detail-banner.png`(1920×136, 20KB)의 실측 색을 옮긴 것이다.
 * 가로로 완전 균일한 세로 그라데이션이라 이미지가 담고 있던 정보는 사실상 이 값들뿐이었다.
 *
 * 스톱이 6개인 이유는 원본이 **선형이 아니라 이징**이기 때문이다 — 양 끝만 잡고 선형으로
 * 이으면 중간에서 13%(R채널 34/255)까지 벌어져 톤이 눈에 띄게 달라진다. 이 스톱들로는
 * 오차가 3% 이하다.
 *
 * ⚠️ `#259EFF` 는 ZUS 토큰이 아니다(`Blue400 #3DA9FF`·`Blue500 #0D94FF` 사이 어디에도
 * 없다). 원본 PNG 값을 그대로 옮겨 화면이 바뀌지 않게 두었고, 토큰을 벗어난 것이 의도인지는
 * 디자이너 확인이 필요하다.
 */
const DEFAULT_BANNER_BACKGROUND = ['linear-gradient(180deg', '#259EFF 0%', '#259EFF 20%', '#41AAFF 40%', '#80C6FF 60%', '#D2EBFF 80%', '#FFFFFF 100%)'].join(', ');
export interface DetailHeroBannerProps {
  /**
   * 배너 이미지 URL.
   *
   * **없을 수 있다.** 맵 템플릿이 없는 퀴즈(`mapTemplateBannerImage` 는 optional)와 맵 자체가
   * 없는 폴더가 그렇다. 그때는 이 컴포넌트가 기본 그라데이션 배경을 그린다 — 예전에는
   * 퀴즈가 `|| ''` 로 빈 문자열을 넘기고(= `<img src="">`) 폴더는 더미 PNG 를 만들어 끼우는
   * 식으로 화면마다 따로 때웠다.
   */
  bannerUrl?: string;
  /**
   * 이미지의 alt 텍스트.
   * 장식용 배너라면 빈 문자열을 넘긴다 — 접근성 트리에서 빠진다.
   */
  alt?: string;
  /** 추가 스타일링을 위한 선택적 className */
  className?: string;
}

/**
 * 상세 화면 상단의 전체 너비 배너.
 *
 * 퀴즈 상세(`/quiz/[id]`)와 폴더 상세(`/folder/[id]`)가 **같은 골격**을 쓴다 —
 * 배너 아래로 히어로 카드가 약 60px 겹쳐 올라오고, 그 겹치는 구간의 텍스트 가독성을
 * 하단 흰색 그라데이션이 받쳐 준다. 높이·`object-fit`·그라데이션 값이 한 세트의 결정이라
 * 여기서 **한 곳이 소유**한다.
 *
 * 원래 `pages/quiz/[id]/components/MapTemplateBanner` 였다. 폴더 상세가 같은 배너를 쓰게
 * 되면서 승격했다 — 각자 두었더니 `relative` 누락과 그라데이션 농도(0.4 vs 0.6)가
 * 조용히 어긋났다. 주석으로 "같게 유지" 하는 방식이 실패한 자리다.
 *
 * 페이지마다 다른 건 **이미지 URL 하나뿐**이다.
 *  - 퀴즈: 맵 템플릿마다 다른 동적 URL. 맵이 없으면 URL 도 없다
 *  - 폴더: 맵이라는 개념이 없어 항상 없다
 *
 * @example
 * ```tsx
 * <DetailHeroBanner bannerUrl={quizData.mapTemplateBannerImage} alt={templateName} />
 * <DetailHeroBanner />
 * ```
 */
export const DetailHeroBanner = ({
  bannerUrl,
  alt = '',
  className
}: DetailHeroBannerProps) => {
  const frameClassName = cn('relative h-[86px] w-full overflow-hidden tablet-sm:h-[136px]', className);

  /*
    이미지가 없으면 배경만 그린다.
     `<img>` 를 `object-cover` 로 늘리지 않으므로 **폭과 무관하게 항상 컨테이너 높이에 딱
    맞는다** — 이미지였을 때는 1920px 보다 넓은 화면에서 세로가 잘려(2560px 에서 45px)
    흰색으로 끝나야 할 아래쪽이 하늘색에서 끊겼다.
     가독성 오버레이도 두지 않는다. 그건 맵 아트워크 위 텍스트를 위한 장치인데, 이 배경은
    이미 흰색으로 끝나 흰색 위에 흰색을 얹는 셈이다.
  */
  if (!bannerUrl) {
    return <div className={frameClassName} style={{
      background: DEFAULT_BANNER_BACKGROUND
    }} aria-hidden />;
  }
  return <div className={frameClassName} data-sentry-component="DetailHeroBanner" data-sentry-source-file="DetailHeroBanner.tsx">
      <img src={bannerUrl} alt={alt}
    // alt 가 빈 문자열이면 장식용이라는 뜻이라 접근성 트리에서 뺀다.
    aria-hidden={alt === '' || undefined} className={'h-full w-full object-cover object-center'} />
      {/* 겹치는 콘텐츠의 텍스트 가독성 향상을 위한 그라데이션 오버레이 */}
      <div className={'absolute inset-x-0 bottom-0 h-20'} style={{
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 100%)'
    }} />
    </div>;
};